import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import logo from '../logo.png';
import { verifyEmail } from '../services/userService';

const REDIRECT_DELAY_MS = 2500;

export default function VerifyEmailScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const token = new URLSearchParams(location.search).get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'pending');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let active = true;
    let timeoutId;

    const runVerification = async () => {
      try {
        const result = await verifyEmail({ token });
        if (!active) {
          return;
        }

        setVerifiedEmail(result?.email || '');
        setStatus('success');
        timeoutId = window.setTimeout(() => {
          navigate('/', { replace: true });
        }, REDIRECT_DELAY_MS);
      } catch (verificationError) {
        if (!active) {
          return;
        }

        setError(verificationError.message);
        setStatus('error');
      }
    };

    runVerification();

    return () => {
      active = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [navigate, token]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: 128,
              height: 128,
              borderRadius: '50%',
              bgcolor: 'common.white',
              p: 1,
            }}
          />

          {status === 'verifying' && <CircularProgress />}
          {status === 'success' && <CheckCircleRoundedIcon color="success" sx={{ fontSize: 64 }} />}
          {(status === 'pending' || status === 'error') && (
            <MailOutlineRoundedIcon color="primary" sx={{ fontSize: 64 }} />
          )}

          {status === 'pending' && (
            <>
              <Stack spacing={1}>
                <Typography variant="h5" component="h1" fontWeight={700}>
                  Verify your email
                </Typography>
                <Typography color="text.secondary">
                  We sent a verification link to {email ? <strong>{email}</strong> : 'your email address'}.
                </Typography>
              </Stack>

              <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
                Open the message and follow the verification link before signing in.
              </Alert>

              <Button component={RouterLink} to="/" variant="contained" size="large" fullWidth>
                Back to sign in
              </Button>
            </>
          )}

          {status === 'verifying' && (
            <Stack spacing={1}>
              <Typography variant="h5" component="h1" fontWeight={700}>
                Verifying your email
              </Typography>
              <Typography color="text.secondary">
                Hold on while we confirm your email address.
              </Typography>
            </Stack>
          )}

          {status === 'success' && (
            <>
              <Stack spacing={1}>
                <Typography variant="h5" component="h1" fontWeight={700}>
                  Verification successful
                </Typography>
                <Typography color="text.secondary">
                  {verifiedEmail ? (
                    <>Your email <strong>{verifiedEmail}</strong> has been verified.</>
                  ) : (
                    'Your email has been verified.'
                  )}
                </Typography>
              </Stack>

              <Alert severity="success" sx={{ width: '100%', textAlign: 'left' }}>
                Redirecting you to sign in.
              </Alert>

              <Button component={RouterLink} to="/" variant="contained" size="large" fullWidth>
                Sign in now
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Stack spacing={1}>
                <Typography variant="h5" component="h1" fontWeight={700}>
                  Verification failed
                </Typography>
                <Typography color="text.secondary">
                  This verification link is invalid or expired.
                </Typography>
              </Stack>

              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                {error || 'Email verification failed.'}
              </Alert>

              <Button component={RouterLink} to="/" variant="contained" size="large" fullWidth>
                Back to sign in
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
